import { toolLabel } from '../toolLabels';
import type { AgentActivityEvent } from '../useChatStore';

/**
 * Pure view-model derivation for the "Agent activity" panel (AID-362).
 *
 * Turns the ordered {@link AgentActivityEvent} log — itself a deterministic
 * projection of the agent SDK stream — into the timeline the `AgentActivity`
 * component renders. A pure function of its inputs (no React, no SDK types), so
 * it is unit-testable with fixture streams and no SDK coupling leaks into the
 * component (AID-362 acceptance).
 *
 * Responsibilities:
 *  - preserve stream order and interleave thinking + tool steps (feasibility #11)
 *  - nest a subagent's steps under its launching `Task` call by
 *    `parentToolUseId` (AID-365)
 *  - collapse consecutive same-name tool calls into an `×N` group (AID-364)
 *  - derive per-call duration from the client start/end stamps (AID-367)
 *  - classify success / running / error / skipped, with safe copy (AID-368)
 */

/** The Agent SDK's built-in subagent-launcher tool. */
const SUBAGENT_TOOL_NAME = 'Task';

export type ActivityStatus = 'success' | 'running' | 'error' | 'skipped';

export interface ThinkingNode {
  kind: 'thinking';
  id: string;
  text: string;
  redacted: boolean;
  status: 'success';
}

export interface ToolNode {
  kind: 'tool';
  id: string;
  toolUseId: string;
  name: string;
  label: string;
  input?: unknown;
  result?: string;
  resultTruncated: boolean;
  status: ActivityStatus;
  /** Wall-clock ms from call start to result, when both stamps are present. */
  durationMs?: number;
  /** Human-readable denial reason when `status === 'skipped'` (AID-368). */
  skippedLabel?: string;
}

export interface ToolGroupNode {
  kind: 'tool-group';
  id: string;
  name: string;
  label: string;
  count: number;
  status: ActivityStatus;
  calls: ToolNode[];
}

export interface SubagentNode {
  kind: 'subagent';
  id: string;
  toolUseId: string;
  subagentType: string;
  label: string;
  taskDescription?: string;
  status: ActivityStatus;
  steps: ActivityNode[];
}

export interface TurnErrorNode {
  kind: 'turn-error';
  id: string;
  label: string;
  status: 'error';
}

export type ActivityNode =
  | ThinkingNode
  | ToolNode
  | ToolGroupNode
  | SubagentNode
  | TurnErrorNode;

export interface AgentActivityModel {
  nodes: ActivityNode[];
  summary: { tools: number; subagents: number; thoughts: number };
  /** The turn is still in flight — drives the live spinner affordances. */
  isRunning: boolean;
  /** Real label of the currently-running step, when one is running. */
  runningLabel?: string;
}

/**
 * Safe, human-readable copy for a `SDKPermissionDeniedMessage.decision_reason_type`
 * category (AID-368). Client-authored — the wire carries only the safe code.
 */
const DENIAL_REASON_LABELS: Record<string, string> = {
  rule: 'Blocked by a policy rule',
  mode: 'Not permitted in read-only mode',
  classifier: 'Blocked by a safety check',
  safetyCheck: 'Blocked by a safety check',
  asyncAgent: 'Denied by an approval agent',
  hook: 'Blocked by a policy hook',
  permissionPromptTool: 'Permission not granted',
  subcommandResults: 'A sub-command was not permitted',
  sandboxOverride: 'Not permitted by the sandbox',
  workingDir: 'Not permitted for this location',
  other: 'Skipped',
  denied: 'Skipped',
};

export const denialReasonLabel = (reasonCode: string): string =>
  DENIAL_REASON_LABELS[reasonCode] ?? 'Skipped';

/**
 * Safe, human-readable copy for a turn-level failure code (AID-368). Maps the
 * agent's documented safe codes + the gateway's opaque error codes to copy;
 * never surfaces raw provider/SDK detail.
 */
const TURN_ERROR_LABELS: Record<string, string> = {
  error_max_turns: 'The agent reached its step limit for this turn.',
  error_max_budget_usd: 'The agent reached its cost limit for this turn.',
  error_during_execution: 'The agent ran into a problem completing this turn.',
  error_max_structured_output_retries:
    'The agent could not produce a valid result.',
  session_budget_exceeded: 'This conversation reached its usage limit.',
  provider_error: 'The AI provider returned an error.',
  rate_limited: 'The agent is being rate-limited — please try again shortly.',
  agent_unavailable: 'The agent is currently unavailable.',
};

export const turnErrorLabel = (code: string): string =>
  TURN_ERROR_LABELS[code] ?? 'The agent ran into a problem.';

interface ToolTerminal {
  isError: boolean;
  result?: string;
  resultTruncated: boolean;
  endedAt: string;
}

const worstStatus = (statuses: ActivityStatus[]): ActivityStatus => {
  if (statuses.includes('error')) {
    return 'error';
  }
  if (statuses.includes('running')) {
    return 'running';
  }
  if (statuses.length > 0 && statuses.every((s) => s === 'skipped')) {
    return 'skipped';
  }

  return 'success';
};

const durationBetween = (
  startedAt: string,
  endedAt: string
): number | undefined => {
  const ms = new Date(endedAt).getTime() - new Date(startedAt).getTime();

  return Number.isFinite(ms) && ms >= 0 ? ms : undefined;
};

/**
 * Collapse runs of consecutive same-name plain tool calls into `×N` groups
 * (AID-364). Thinking, subagents, denied and turn-error nodes break a run; a
 * run of one stays a plain node.
 */
const groupConsecutive = (nodes: ActivityNode[]): ActivityNode[] => {
  const result: ActivityNode[] = [];
  let run: ToolNode[] = [];

  const flush = (): void => {
    if (run.length === 0) {
      return;
    }
    if (run.length === 1) {
      result.push(run[0]);
    } else {
      const [first] = run;
      result.push({
        kind: 'tool-group',
        id: `group:${first.toolUseId}`,
        name: first.name,
        label: first.label,
        count: run.length,
        status: worstStatus(run.map((call) => call.status)),
        calls: run,
      });
    }
    run = [];
  };

  for (const node of nodes) {
    const groupable = node.kind === 'tool' && node.status !== 'skipped';
    if (groupable && (run.length === 0 || run[0].name === node.name)) {
      run.push(node);
    } else {
      flush();
      if (groupable) {
        run.push(node);
      } else {
        result.push(node);
      }
    }
  }
  flush();

  return result;
};

export const buildAgentActivityModel = (
  events: AgentActivityEvent[],
  isStreaming: boolean
): AgentActivityModel => {
  // Tool results keyed by tool_use id (a complete may arrive at any nesting
  // level, so correlate up front before placing nodes). Denials are read
  // straight from their event where the node is built.
  const terminals = new Map<string, ToolTerminal>();
  for (const event of events) {
    if (event.kind === 'tool_complete') {
      terminals.set(event.toolUseId, {
        isError: event.isError,
        result: event.result,
        resultTruncated: event.resultTruncated ?? false,
        endedAt: event.endedAt,
      });
    }
  }

  // Child events keyed by their parent tool_use id (subagent grouping).
  const parentOf = (event: AgentActivityEvent): string | null => {
    if (
      event.kind === 'thinking' ||
      event.kind === 'tool_start' ||
      event.kind === 'tool_denied'
    ) {
      return event.parentToolUseId ?? null;
    }

    return null;
  };

  let thoughts = 0;
  let tools = 0;

  const buildLevel = (parentId: string | null): ActivityNode[] => {
    const nodes: ActivityNode[] = [];

    for (let i = 0; i < events.length; i++) {
      const event = events[i];

      if (event.kind === 'thinking' && parentOf(event) === parentId) {
        const redacted = event.redacted ?? false;
        // Hide empty non-redacted thinking (AID-366: hide, don't show a blank
        // "Thought" row); redacted frames still render the neutral placeholder.
        if (!redacted && event.text.trim().length === 0) {
          continue;
        }
        thoughts += 1;
        nodes.push({
          kind: 'thinking',
          id: `think:${parentId ?? 'root'}:${i}`,
          text: event.text,
          redacted,
          status: 'success',
        });
        continue;
      }

      if (event.kind === 'tool_denied' && parentOf(event) === parentId) {
        tools += 1;
        nodes.push({
          kind: 'tool',
          id: `tool:${event.toolUseId}`,
          toolUseId: event.toolUseId,
          name: event.name,
          label: toolLabel(event.name),
          resultTruncated: false,
          status: 'skipped',
          skippedLabel: denialReasonLabel(event.reasonCode),
        });
        continue;
      }

      if (event.kind === 'tool_start' && parentOf(event) === parentId) {
        // A subagent launcher: render as a subagent node with its own steps.
        if (event.name === SUBAGENT_TOOL_NAME) {
          const steps = buildLevel(event.toolUseId);
          const terminal = terminals.get(event.toolUseId);
          const childStatuses = steps.map((step) => step.status);
          const status: ActivityStatus = terminal?.isError
            ? 'error'
            : worstStatus(
                terminal ? childStatuses : [...childStatuses, 'running']
              );
          const subagentType =
            event.subagentType ?? readSubagentType(event.input) ?? 'subagent';
          nodes.push({
            kind: 'subagent',
            id: `subagent:${event.toolUseId}`,
            toolUseId: event.toolUseId,
            subagentType,
            label: toTitle(subagentType),
            taskDescription: readTaskDescription(event.input),
            status,
            steps,
          });
          continue;
        }

        tools += 1;
        const terminal = terminals.get(event.toolUseId);
        const status: ActivityStatus = terminal
          ? terminal.isError
            ? 'error'
            : 'success'
          : 'running';
        nodes.push({
          kind: 'tool',
          id: `tool:${event.toolUseId}`,
          toolUseId: event.toolUseId,
          name: event.name,
          label: toolLabel(event.name),
          input: event.input,
          // A failed tool's result may carry raw provider/SDK detail — never
          // surface it (AID-368 / RSP-6227). The red node + "failed" label is
          // the client-authored safe copy; successful results show in full.
          result: terminal?.isError ? undefined : terminal?.result,
          resultTruncated: terminal?.isError
            ? false
            : (terminal?.resultTruncated ?? false),
          status,
          durationMs: terminal
            ? durationBetween(event.startedAt, terminal.endedAt)
            : undefined,
        });
        continue;
      }

      // turn_error is always top level and terminal.
      if (event.kind === 'turn_error' && parentId === null) {
        nodes.push({
          kind: 'turn-error',
          id: `turn-error:${i}`,
          label: turnErrorLabel(event.code),
          status: 'error',
        });
      }
    }

    return groupConsecutive(nodes);
  };

  const nodes = buildLevel(null);
  const subagents = nodes.filter((node) => node.kind === 'subagent').length;

  const runningNode = findRunning(nodes);
  const isRunning =
    isStreaming && (runningNode !== undefined || !hasTurnEnd(nodes));

  return {
    nodes,
    summary: { tools, subagents, thoughts },
    isRunning,
    runningLabel: runningNode?.label,
  };
};

const hasTurnEnd = (nodes: ActivityNode[]): boolean =>
  nodes.some((node) => node.kind === 'turn-error');

const findRunning = (nodes: ActivityNode[]): { label: string } | undefined => {
  for (const node of nodes) {
    if (node.kind === 'tool' && node.status === 'running') {
      return { label: node.label };
    }
    if (node.kind === 'tool-group' && node.status === 'running') {
      return { label: node.label };
    }
    if (node.kind === 'subagent') {
      if (node.status === 'running') {
        const inner = findRunning(node.steps);

        return inner ?? { label: node.label };
      }
    }
  }

  return undefined;
};

/** Read `subagent_type` from a `Task` tool call's input, defensively. */
const readSubagentType = (input: unknown): string | undefined =>
  readStringField(input, 'subagent_type');

const readTaskDescription = (input: unknown): string | undefined =>
  readStringField(input, 'description');

const readStringField = (input: unknown, field: string): string | undefined => {
  if (typeof input === 'object' && input !== null && field in input) {
    const value = (input as Record<string, unknown>)[field];

    return typeof value === 'string' && value.length > 0 ? value : undefined;
  }

  return undefined;
};

const toTitle = (value: string): string =>
  value
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();
